import * as THREE from 'three';

const THICKNESS = 0.022;
const MIN_SIZE = 0.004;

interface Shell {
  source: THREE.Mesh;
  mesh: THREE.Mesh;
  /** Local transform that grows the box around its own center. */
  local: THREE.Matrix4;
}

/**
 * Outlines whatever you're about to use: every mesh of the object gets a slightly
 * bigger back-faced copy in a glowing color, which shows as a rim around its edges.
 */
export class Highlighter {
  private readonly group = new THREE.Group();
  private readonly material = new THREE.MeshBasicMaterial({
    color: '#ffd400',
    side: THREE.BackSide,
    transparent: true,
    opacity: 0.9,
    fog: false,
  });
  private shells: Shell[] = [];
  private target: THREE.Object3D | null = null;

  constructor(scene: THREE.Scene) {
    this.group.matrixAutoUpdate = false;
    scene.add(this.group);
  }

  get current(): THREE.Object3D | null {
    return this.target;
  }

  set(obj: THREE.Object3D | null): void {
    if (obj === this.target) return;
    this.target = obj;
    for (const s of this.shells) this.group.remove(s.mesh);
    this.shells = [];
    if (!obj) return;
    obj.updateWorldMatrix(true, true);
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    const scale = new THREE.Vector3();
    obj.traverseVisible((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh || m.userData.noOutline) return;
      const geo = m.geometry;
      if (!geo.boundingBox) geo.computeBoundingBox();
      const bb = geo.boundingBox;
      if (!bb) return;
      bb.getSize(size);
      bb.getCenter(center);
      // Flat things (planes, decals) don't outline well: skip them.
      if (size.x < MIN_SIZE || size.y < MIN_SIZE || size.z < MIN_SIZE) return;
      scale.setFromMatrixScale(m.matrixWorld);
      const grow = (axis: number, s: number): number =>
        1 + (2 * THICKNESS) / Math.max(1e-4, axis * Math.abs(s));
      const local = new THREE.Matrix4()
        .makeTranslation(center.x, center.y, center.z)
        .multiply(
          new THREE.Matrix4().makeScale(grow(size.x, scale.x), grow(size.y, scale.y), grow(size.z, scale.z)),
        )
        .multiply(new THREE.Matrix4().makeTranslation(-center.x, -center.y, -center.z));
      const shell = new THREE.Mesh(geo, this.material);
      shell.matrixAutoUpdate = false;
      shell.castShadow = shell.receiveShadow = false;
      this.group.add(shell);
      this.shells.push({ source: m, mesh: shell, local });
    });
    this.sync();
  }

  /** Follow the object if it moves, and pulse a little. */
  update(time: number): void {
    if (!this.target) return;
    this.material.opacity = 0.65 + Math.sin(time * 5) * 0.25;
    this.sync();
  }

  private sync(): void {
    for (const s of this.shells) {
      s.source.updateWorldMatrix(true, false);
      s.mesh.matrix.multiplyMatrices(s.source.matrixWorld, s.local);
      s.mesh.matrixWorld.copy(s.mesh.matrix);
      s.mesh.visible = s.source.visible;
    }
  }
}
